import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageIntro, PhotoBanner, Reveal, Container, SectionTitle, Img } from "@/components/common/Primitives";
import { useLang } from "@/lib/i18n";
import { useContent } from "@/lib/content";
import { usePageMeta } from "@/lib/seo";
import { SIZES } from "@/lib/images";

export default function About() {
  const { lang, tx, tr } = useLang();
  const { data } = useContent();
  usePageMeta(tx("Om oss", "About us"), tx("Fitjar Fjordhotell sidan 1986 – eit familiedrive hotell ved Fitjarvika.", "Fitjar Fjordhotell since 1986 – a family-run hotel on Fitjarvika."));
  const story = data?.story || {};
  const people = story.people || [];
  const timeline = story.timeline || [];

  return (
    <div data-testid="about-page">
      <PageIntro
        testid="about-hero"
        title={tx("Fitjar Fjordhotell sidan 1986", "Fitjar Fjordhotell since 1986")}
        lead={tr(story.intro) || tx("Me er eit familiedrive hotell. Her møter du Ronald og Anne Marie, saman med dei som lagar maten og gjer romma klare.", "We are a family-run hotel. Here you meet Ronald and Anne Marie, together with the people who cook the food and make the rooms ready.")}
      />
      <PhotoBanner src="/images/hotel-spring-river.jpg" alt={tx("Fitjar Fjordhotell ein vårdag", "Fitjar Fjordhotell on a spring day")} position="50% 60%" testid="about-banner" className="mt-10 lg:mt-12" />

      {/* Story */}
      <section className="py-20 lg:py-28" data-testid="about-story">
        <Container className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          <Reveal className="lg:col-span-5">
            <SectionTitle size="lg" title={tx("Frå eitt hus til tre", "From one house to three")} />
            <ol className="mt-10 divide-y divide-border border-y border-border" data-testid="about-timeline">
              {timeline.map((t) => (
                <li key={t.year} className="grid gap-1 py-5 sm:grid-cols-[9rem_1fr] sm:gap-6">
                  <p className="text-[1.0625rem] font-semibold text-foreground tabular">{t.year}</p>
                  <p className="text-[0.9375rem] leading-relaxed text-muted-foreground">{lang === "en" ? t.en : t.nn}</p>
                </li>
              ))}
            </ol>
          </Reveal>
          <Reveal className="lg:col-span-7" delay={0.05}>
            <div className="grid grid-cols-5 gap-3 sm:gap-4">
              <Img src="/images/hotel-entrance.jpg" alt={tx("Inngangen til Fitjar Fjordhotell", "The entrance to Fitjar Fjordhotell")} className="col-span-3 aspect-[4/5]" radius="rounded-[22px]" sizes={SIZES.half} position="50% 40%" />
              <div className="col-span-2 flex flex-col gap-3 pt-10 sm:gap-4 sm:pt-14">
                <Img src="/images/sign-flowers.jpg" alt={tx("Skiltet ved inngangen", "The sign by the entrance")} className="aspect-[3/4]" radius="rounded-[18px]" sizes={SIZES.quarter} />
                <Img src="/images/view-garden.jpg" alt={tx("Hagen ved hotellet", "The garden by the hotel")} className="aspect-square" radius="rounded-[18px]" sizes={SIZES.quarter} />
              </div>
            </div>
          </Reveal>
        </Container>
      </section>

      {/* People */}
      <section className="bg-secondary/70 py-20 lg:py-28" data-testid="about-people">
        <Container>
          <SectionTitle size="lg" title={tx("Dei du møter", "The people you meet")} />
          <div className="mt-10 grid gap-x-7 gap-y-10 grid-cols-2 lg:grid-cols-4">
            {people.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.05}>
                <figure data-testid={`about-person-${i}`}>
                  <Img src={p.image} alt={p.name} className="aspect-[4/5]" radius="rounded-[22px]" imgClassName="object-top" sizes={SIZES.quarter} />
                  <figcaption className="mt-4">
                    <p className="text-[1.0625rem] font-semibold text-foreground">{p.name}</p>
                    <p className="mt-0.5 text-sm text-muted-foreground">{tr(p.role)}</p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* Closing */}
      <section className="py-20 lg:py-28" data-testid="about-quote">
        <Container className="grid gap-10 lg:grid-cols-12 lg:items-end lg:gap-16">
          <Reveal className="lg:col-span-7">
            <p className="text-[2.25rem] font-bold leading-[1.0] tracking-[-0.035em] text-foreground sm:text-[3rem] lg:text-[3.5rem]">«Fitjar – nett so poteto!»</p>
          </Reveal>
          <Reveal className="lg:col-span-5" delay={0.05}>
            <p className="text-lg text-muted-foreground">{tx("Velkomen til oss.", "Welcome.")}</p>
            <Button asChild size="lg" className="mt-5" data-testid="about-book-cta"><Link to="/bestill">{tx("Sjå ledige rom", "See available rooms")} <ArrowRight className="h-4 w-4" /></Link></Button>
          </Reveal>
        </Container>
      </section>
    </div>
  );
}
